import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { Link } from 'react-router-dom'
import { apiConstants, getGeneric, postMultipart } from '../../common/constants'
import { formatCurrency, formatDate } from '../../helpers/UtilityHelper'

const MAX_FILE_BYTES = 100 * 1024 * 1024
const ALLOWED_EXT = [
  'mp4', 'mov', 'avi', 'webm',
  'mp3', 'aac', 'ogg', 'wav', 'm4a',
  'jpg', 'jpeg', 'png', 'gif', 'webp',
  'pdf',
]

// Status → eligible claim type mapping
// DNR is valid for RTO_DELIVERED too (marked returned but warehouse never received it) —
// backend TAT logic uses latest(eddRTO, actual RTO delivery) for that case.
// EXCHANGE_DELIVERED is treated as RVP by the backend (resolveShipmentDirection).
const STATUS_CLAIM_MAP = {
  DELIVERED: ['DNR', 'DAMAGED', 'WRONG_ITEM', 'PARTIAL', 'EMPTY'],
  RTO_DELIVERED: ['DNR', 'DAMAGED', 'WRONG_ITEM', 'PARTIAL', 'EMPTY'],
  EXCHANGE_DELIVERED: ['DNR', 'DAMAGED', 'WRONG_ITEM', 'PARTIAL', 'EMPTY'],
  PICKUP_PENDING: ['HANDOVER_DISPUTE'],
  CANCELLED_ORDER: ['HANDOVER_DISPUTE', 'LOST'],
}

const CLAIM_TYPES = [
  {
    key: 'DNR',
    title: 'Delivered Not Received',
    desc: 'Marked Delivered but consignee / warehouse never received the shipment.',
    legs: ['FORWARD', 'RTO', 'RVP'],
    color: '#c62828',
    bg: '#fff5f5',
    Icon: LocalShippingOutlinedIcon,
  },
  {
    key: 'DAMAGED',
    title: 'Damaged Parcel',
    desc: 'Consignee or warehouse received a physically damaged product.',
    legs: ['FORWARD', 'RTO', 'RVP'],
    color: '#e65100',
    bg: '#fff8f0',
    Icon: ReportProblemOutlinedIcon,
  },
  {
    key: 'WRONG_ITEM',
    title: 'Wrong Item Received',
    desc: 'Item received does not match the ordered product.',
    legs: ['FORWARD', 'RTO', 'RVP'],
    color: '#6a1b9a',
    bg: '#fdf4ff',
    Icon: SwapHorizIcon,
  },
  {
    key: 'PARTIAL',
    title: 'Partial Delivery',
    desc: 'Only some SKUs / units were received.',
    legs: ['FORWARD', 'RTO', 'RVP'],
    color: '#1565c0',
    bg: '#f0f5ff',
    Icon: RemoveCircleOutlinedIcon,
  },
  {
    key: 'EMPTY',
    title: 'Empty Parcel',
    desc: 'Parcel received but the product was missing inside.',
    legs: ['FORWARD', 'RTO', 'RVP'],
    color: '#2e7d32',
    bg: '#f0faf0',
    Icon: InboxOutlinedIcon,
  },
  {
    key: 'NEVER_PICKED',
    title: 'Never Picked',
    desc: 'Forward shipment / RVP was never picked by the courier.',
    legs: ['FORWARD', 'RVP'],
    color: '#4e342e',
    bg: '#faf5f3',
    Icon: BlockOutlinedIcon,
  },
  {
    key: 'LOST',
    title: 'Lost Shipment',
    desc: 'No scan activity for an extended period, shipment not delivered.',
    legs: ['FORWARD', 'RTO', 'RVP', 'HANDOVER'],
    color: '#00695c',
    bg: '#f0faf8',
    Icon: SearchOutlinedIcon,
  },
  {
    key: 'HANDOVER_DISPUTE',
    title: 'Handover Dispute',
    desc: 'Courier picked up but showing never picked, or cancelled shipment but courier picked up.',
    legs: ['HANDOVER'],
    color: '#bf360c',
    bg: '#fbe9e7',
    Icon: HandshakeOutlinedIcon,
  },
]

const PROOF_REQUIREMENTS = {
  FORWARD: {
    DNR: [
      { key: 'VOC', label: 'Consignee VOC (audio/video)', required: true, accept: 'audio/*,video/*', help: 'Recording where consignee disputes delivery' },
      { key: 'ALT_MOBILE', label: 'Alternate mobile number of consignee', required: true, isText: true, help: '10-digit mobile number' },
    ],
    DAMAGED: [
      { key: 'PRE_SHIP_PROOF', label: 'Pre-shipping packaging video / image', required: true, accept: 'image/*,video/*', help: 'AWB clearly visible on package' },
      { key: 'UNBOXING_PROOF', label: 'Customer unboxing video / image', required: true, accept: 'image/*,video/*', help: 'AWB visible during unboxing' },
    ],
    WRONG_ITEM: [
      { key: 'PRE_SHIP_PROOF', label: 'Pre-shipping packaging proof', required: true, accept: 'image/*,video/*' },
      { key: 'UNBOXING_PROOF', label: 'Unboxing proof showing wrong item', required: true, accept: 'image/*,video/*' },
    ],
    PARTIAL: [
      { key: 'PRE_SHIP_PROOF', label: 'Pre-shipping packaging proof', required: true, accept: 'image/*,video/*' },
      { key: 'UNBOXING_PROOF', label: 'Unboxing proof', required: true, accept: 'image/*,video/*' },
    ],
    EMPTY: [
      { key: 'PRE_SHIP_PROOF', label: 'Pre-shipping packaging proof', required: true, accept: 'image/*,video/*' },
      { key: 'UNBOXING_PROOF', label: 'Unboxing proof of empty parcel', required: true, accept: 'image/*,video/*' },
    ],
    NEVER_PICKED: [],
    LOST: [],
  },
  RTO: {
    DNR: [
      { key: 'WAREHOUSE_POD', label: 'Complete Warehouse POD (PDF)', required: true, accept: '.pdf', help: 'Front + intermediate + last pages. AWB highlighted. Marked "Not Received".' },
    ],
    DAMAGED: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing video / image', required: true, accept: 'image/*,video/*', help: 'AWB visible on package' },
    ],
    WRONG_ITEM: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof', required: true, accept: 'image/*,video/*' },
    ],
    PARTIAL: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof', required: true, accept: 'image/*,video/*' },
    ],
    EMPTY: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof of empty parcel', required: true, accept: 'image/*,video/*' },
    ],
    LOST: [],
  },
  RVP: {
    DNR: [
      { key: 'WAREHOUSE_POD', label: 'Complete Warehouse POD (PDF)', required: true, accept: '.pdf', help: 'Front + intermediate + last pages. AWB highlighted. Marked "Not Received".' },
    ],
    DAMAGED: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing video', required: true, accept: 'image/*,video/*' },
    ],
    WRONG_ITEM: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof', required: true, accept: 'image/*,video/*' },
    ],
    PARTIAL: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof', required: true, accept: 'image/*,video/*' },
    ],
    EMPTY: [
      { key: 'UNBOXING_PROOF', label: 'Return unboxing proof of empty parcel', required: true, accept: 'image/*,video/*' },
    ],
    NEVER_PICKED: [],
    LOST: [],
  },
  HANDOVER: {
    HANDOVER_DISPUTE: [
      { key: 'MANIFEST_COPY', label: 'Manifest copy showing AWB', required: true, accept: 'image/*,.pdf', help: 'Upload the manifest copy showing the disputed AWB was handed over to the courier' },
      { key: 'HANDOVER_PROOF', label: 'Handover proof (photo/video)', required: false, accept: 'image/*,video/*', help: 'Any additional proof of handover to courier partner' },
    ],
    LOST: [],
  },
}

const STEPS = [
  { label: 'Shipment', description: 'Verify shipment details' },
  { label: 'Claim Type', description: 'Select issue category' },
  { label: 'Proofs', description: 'Upload evidence' },
  { label: 'Review', description: 'Submit your claim' },
]

const fileIconFor = (file) => {
  const name = file?.name || ''
  const ext = name.split('.').pop()?.toLowerCase()
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return <VideocamOutlinedIcon fontSize="small" />
  if (['mp3', 'aac', 'ogg', 'wav', 'm4a'].includes(ext)) return <AudiotrackOutlinedIcon fontSize="small" />
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageOutlinedIcon fontSize="small" />
  if (ext === 'pdf') return <PictureAsPdfOutlinedIcon fontSize="small" />
  return <DescriptionOutlinedIcon fontSize="small" />
}

const formatBytes = (b) => {
  if (!b && b !== 0) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Compact key-value row used in info cards ────────────────────────────────
const InfoRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.6, borderBottom: '1px solid', borderColor: 'grey.100' }}>
    <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', mr: 2 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, fontWeight: 500, textAlign: 'right' }}>
      {value ?? '—'}
    </Typography>
  </Box>
)

// ─── File dropzone ────────────────────────────────────────────────────────────
function FileDropzone({ accept, onDrop, disabled, multiple = true }) {
  const inputRef = useRef(null)
  const [drag, setDrag] = useState(false)

  return (
    <Box
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDrag(false)
        if (disabled) return
        onDrop(Array.from(e.dataTransfer.files || []))
      }}
      sx={{
        border: '1.5px dashed',
        borderColor: drag ? 'primary.main' : 'grey.300',
        borderRadius: 1.5,
        py: 2,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: drag ? 'primary.50' : 'grey.50',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 120ms ease',
        opacity: disabled ? 0.5 : 1,
        '&:hover': !disabled ? { borderColor: 'primary.main', bgcolor: 'primary.50' } : {},
      }}
    >
      <CloudUploadOutlinedIcon sx={{ color: drag ? 'primary.main' : 'text.disabled', flexShrink: 0 }} />
      <Box>
        <Typography variant="body2" fontWeight={500} color={drag ? 'primary.main' : 'text.primary'}>
          Click to upload or drag & drop
        </Typography>
        <Typography variant="caption" color="text.secondary">
          JPG · PNG · PDF · MP4 · MP3 — max 100 MB each
        </Typography>
      </Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept || ALLOWED_EXT.map((e) => `.${e}`).join(',')}
        multiple={multiple}
        onChange={(e) => {
          onDrop(Array.from(e.target.files || []))
          e.target.value = ''
        }}
      />
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function RaiseClaimModal({ open, onClose, shipment, onSuccess, merchantId }) {
  const { enqueueSnackbar } = useSnackbar()
  const [active, setActive] = useState(0)
  const [claimType, setClaimType] = useState('')
  const [proofFiles, setProofFiles] = useState({})
  const [proofTexts, setProofTexts] = useState({})
  const [remarks, setRemarks] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submittedClaim, setSubmittedClaim] = useState(null)
  const [checkingEligibility, setCheckingEligibility] = useState(false)
  const [eligibility, setEligibility] = useState(null)
  const [eligibilityError, setEligibilityError] = useState(null)

  const shipmentType = useMemo(() => {
    // For handover disputes (pickup pending / cancelled), use HANDOVER leg
    const status = (shipment?.orderStatus || '').toUpperCase()
    if (['PICKUP_PENDING', 'CANCELLED_ORDER'].includes(status)) return 'HANDOVER'
    if (status === 'RTO_DELIVERED') return 'RTO'
    if (status === 'EXCHANGE_DELIVERED') return 'RVP'
    const t = (shipment?.shipment_type || shipment?.shipmentType || '').toUpperCase()
    if (t === 'B2B') return 'FORWARD'
    if (['FORWARD', 'RTO', 'RVP'].includes(t)) return t
    if (shipment?.is_reverse || shipment?.isReverse) return 'RVP'
    return 'FORWARD'
  }, [shipment])

  const eligibleClaimTypes = useMemo(() => {
    const status = (shipment?.orderStatus || '').toUpperCase()
    const statusAllowed = STATUS_CLAIM_MAP[status]
    if (statusAllowed) {
      // Filter by status-specific allowed types AND shipment leg
      return CLAIM_TYPES.filter((t) => statusAllowed.includes(t.key) && t.legs.includes(shipmentType))
    }
    // Fallback: filter by shipment leg only
    return CLAIM_TYPES.filter((t) => t.legs.includes(shipmentType))
  }, [shipmentType, shipment])

  const requirements = useMemo(() => {
    if (!claimType) return []
    const effectiveType =
      (eligibility?.shipmentDirection || '').toUpperCase() || shipmentType
    const localMatrix = PROOF_REQUIREMENTS[effectiveType]?.[claimType] || []
    const apiProofs =
      eligibility?.requiredDocs ||
      eligibility?.requiredProofs ||
      eligibility?.proofRequirements ||
      eligibility?.requirements ||
      eligibility?.proofs
    if (Array.isArray(apiProofs) && apiProofs.length) {
      const localByKey = Object.fromEntries(localMatrix.map((r) => [r.key, r]))
      let merged
      if (typeof apiProofs[0] === 'object' && apiProofs[0]?.label) {
        merged = apiProofs.map((r) => ({
          required: true,
          accept: 'image/*,video/*,audio/*,.pdf',
          ...(localByKey[r.key] || {}),
          ...r,
        }))
      } else {
        merged = apiProofs.map((key) =>
          localByKey[key] || {
            key,
            label: key.replace(/_/g, ' '),
            required: true,
            accept: 'image/*,video/*,audio/*,.pdf',
          }
        )
      }
      const mergedKeys = new Set(merged.map((r) => r.key))
      const localOnly = localMatrix.filter((r) => !mergedKeys.has(r.key))
      return [...merged, ...localOnly]
    }
    return localMatrix
  }, [shipmentType, claimType, eligibility])

  useEffect(() => {
    if (!open) {
      setActive(0)
      setClaimType('')
      setProofFiles({})
      setProofTexts({})
      setRemarks('')
      setSubmitting(false)
      setSubmittedClaim(null)
      setCheckingEligibility(false)
      setEligibility(null)
      setEligibilityError(null)
    }
  }, [open])

  useEffect(() => {
    setEligibility(null)
    setEligibilityError(null)
  }, [claimType])

  const handleFiles = (key, list) => {
    const valid = []
    for (const f of list) {
      if (f.size > MAX_FILE_BYTES) {
        enqueueSnackbar(`${f.name} exceeds 100MB and was skipped`, { variant: 'error' })
        continue
      }
      const ext = f.name.split('.').pop()?.toLowerCase()
      if (!ALLOWED_EXT.includes(ext)) {
        enqueueSnackbar(`${f.name} has an unsupported format`, { variant: 'error' })
        continue
      }
      valid.push(f)
    }
    setProofFiles((prev) => {
      const existing = prev[key] || []
      const merged = [...existing, ...valid].slice(0, 5)
      if (existing.length + valid.length > 5) {
        enqueueSnackbar('Max 5 files per proof category', { variant: 'warning' })
      }
      return { ...prev, [key]: merged }
    })
  }

  const removeFile = (key, idx) => {
    setProofFiles((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== idx),
    }))
  }

  const proofsValid = useMemo(() => {
    for (const r of requirements) {
      if (!r.required) continue
      if (r.isText) {
        const val = String(proofTexts[r.key] || '').trim()
        if (!val) return false
        if (r.key === 'ALT_MOBILE' && !/^\d{10}$/.test(val)) return false
      } else {
        if (!(proofFiles[r.key] && proofFiles[r.key].length > 0)) return false
      }
    }
    return true
  }, [requirements, proofTexts, proofFiles])

  const canGoNext = () => {
    if (active === 0) return !!shipment?.awb_number
    if (active === 1) return !!claimType
    if (active === 2) return proofsValid
    return true
  }

  const runEligibilityCheck = async () => {
    if (!shipment?.awb_number || !claimType) return false
    setCheckingEligibility(true)
    setEligibilityError(null)
    try {
      const params = { claimType }
      if (merchantId) params.merchantId = merchantId
      const res = await getGeneric(
        `${apiConstants.CLAIMS_CHECK_ELIGIBILITY}/${shipment.awb_number}`,
        params
      )
      const data = res?.data?.responseObj || res?.data || {}
      const eligibleFlag =
        data.eligible !== undefined
          ? !!data.eligible
          : data.isEligible !== undefined
            ? !!data.isEligible
            : true

      if (!eligibleFlag) {
        let msg
        if (data.claimsEnabled === false) {
          msg = 'Claims are not enabled for your account. Please contact your KAM to enable the module.'
        } else if (data.activeClaimExists === true) {
          msg = 'A claim already exists for this shipment. View it from the Claims page.'
        } else if (data.tatExpired === true) {
          msg = `The claim window has expired for this shipment.${data.tatDeadlineAt ? ` Window closed at ${data.tatDeadlineAt}.` : ''} Contact your KAM for exceptional cases.`
        } else if (data.orderDelivered === false) {
          msg = 'This shipment is not yet delivered. Claims can only be raised after delivery.'
        } else if (data.orderStatusEligible === false) {
          msg = 'This shipment is not in an eligible status for the selected claim type.'
        } else {
          const code = data.errorCode || data.code
          msg = data.message || (
            code === 'CLAIM_WINDOW_EXPIRED' ? 'The claim window has expired for this shipment.' :
            code === 'CLAIM_ALREADY_EXISTS' ? 'A claim of this type already exists for this AWB.' :
            'This shipment is not eligible for the selected claim type.'
          )
        }
        setEligibilityError(msg)
        setEligibility(data)
        return false
      }

      setEligibility(data)
      return true
    } catch (err) {
      const code = err?.response?.data?.errorCode || err?.response?.data?.code
      const msg = err?.response?.data?.message || (
        code === 'CLAIM_WINDOW_EXPIRED' ? 'The claim window has expired for this shipment.' :
        code === 'CLAIM_ALREADY_EXISTS' ? 'A claim of this type already exists for this AWB.' :
        'Failed to verify eligibility. Please try again.'
      )
      setEligibilityError(msg)
      return false
    } finally {
      setCheckingEligibility(false)
    }
  }

  const handleContinue = async () => {
    if (active === 1) {
      if (eligibility) {
        setActive((s) => s + 1)
        return
      }
      const ok = await runEligibilityCheck()
      if (!ok) return
    }
    setActive((s) => s + 1)
  }

  const submit = async () => {
    if (!shipment?.awb_number || !claimType) return
    if (!remarks?.trim()) return
    const form = new FormData()
    form.append('awbNumber', shipment.awb_number)
    form.append('claimType', claimType)
    form.append('remarks', remarks || '')
    if (merchantId) form.append('merchantId', merchantId)
    if (proofTexts.ALT_MOBILE) form.append('alternateMobile', proofTexts.ALT_MOBILE)

    for (const r of requirements) {
      const files = proofFiles[r.key] || []
      for (const f of files) {
        form.append('files', f)
        form.append('fileDescriptions', r.key)
      }
    }

    try {
      setSubmitting(true)
      const res = await postMultipart(apiConstants.CLAIMS_RAISE, form)
      const claim = res?.data?.responseObj || res?.data || {}
      setSubmittedClaim(claim)
      enqueueSnackbar('Claim raised successfully', { variant: 'success' })
      onSuccess && onSuccess(claim)
    } catch (err) {
      const code = err?.response?.data?.errorCode || err?.response?.data?.code
      const msg = err?.response?.data?.message || 'Failed to raise claim'
      if (code === 'CLAIM_WINDOW_EXPIRED') {
        enqueueSnackbar('Claim window has expired for this shipment', { variant: 'error' })
      } else if (code === 'CLAIM_ALREADY_EXISTS') {
        enqueueSnackbar('A claim of this type already exists for this AWB', { variant: 'warning' })
      } else {
        enqueueSnackbar(msg, { variant: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Step progress indicator ────────────────────────────────────────────────
  const renderStepHeader = () => (
    <Box sx={{ px: 4, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {STEPS.map((s, idx) => (
          <Fragment key={s.label}>
            <Stack alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  transition: 'all 0.25s',
                  ...(idx < active
                    ? { bgcolor: 'success.main', color: 'white' }
                    : idx === active
                      ? { bgcolor: 'primary.main', color: 'white', boxShadow: '0 0 0 3px rgba(25,118,210,0.18)' }
                      : { bgcolor: 'grey.100', color: 'text.disabled' }),
                }}
              >
                {idx < active
                  ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                  : idx + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: idx === active ? 700 : 400,
                  color: idx === active ? 'primary.main' : idx < active ? 'success.main' : 'text.disabled',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </Typography>
            </Stack>
            {idx < STEPS.length - 1 && (
              <Box
                sx={{
                  flex: 2,
                  height: 2,
                  mb: 2.5,
                  mx: 0.5,
                  borderRadius: 1,
                  bgcolor: idx < active ? 'success.main' : 'grey.200',
                  transition: 'background-color 0.3s',
                }}
              />
            )}
          </Fragment>
        ))}
      </Box>
    </Box>
  )

  // ─── Step 0: Shipment verify ─────────────────────────────────────────────────
  const renderStep0 = () => (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          Confirm shipment details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All fields are auto-populated. Verify this is the correct shipment.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Shipment
              </Typography>
              <Chip size="small" label={shipmentType} color="primary" variant="outlined" sx={{ ml: 'auto', fontSize: 11, height: 20 }} />
            </Stack>
            <InfoRow label="AWB Number" value={shipment?.awb_number} mono />
            <InfoRow label="Order ID" value={shipment?.reference || shipment?.orderId} mono />
            <InfoRow label="Courier" value={shipment?.courierDetail?.parent || shipment?.courier || '—'} />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Delivery info
              </Typography>
            </Stack>
            <InfoRow
              label="Delivery Date"
              value={shipment?.actualDeliveryTime ? formatDate(shipment.actualDeliveryTime, 'dd MMM yyyy') : '—'}
            />
            <InfoRow
              label="Invoice Value"
              value={shipment?.invoice_value != null ? formatCurrency(shipment.invoice_value) : '—'}
            />
            <InfoRow label="Status" value={shipment?.orderStatus} />
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  )

  // ─── Step 1: Claim type selection ────────────────────────────────────────────
  const renderStep1 = () => (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          What happened with this shipment?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select the issue type. This determines the evidence required in the next step.
        </Typography>
      </Box>

      {eligibilityError && (
        <Alert
          severity="error"
          onClose={() => setEligibilityError(null)}
          sx={{ borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>Shipment not eligible</Typography>
          <Typography variant="body2">{eligibilityError}</Typography>
          {eligibility && (eligibility.deliveryTime || eligibility.tatDeadlineAt) && (
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              {eligibility.deliveryTime && (
                <Typography variant="caption">Delivered: <strong>{eligibility.deliveryTime}</strong></Typography>
              )}
              {eligibility.tatDeadlineAt && (
                <Typography variant="caption">Window closed: <strong>{eligibility.tatDeadlineAt}</strong></Typography>
              )}
            </Stack>
          )}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        {eligibleClaimTypes.map((t) => {
          const selected = claimType === t.key
          const TypeIcon = t.Icon
          return (
            <Grid item xs={12} sm={6} key={t.key}>
              <Paper
                onClick={() => setClaimType(t.key)}
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  height: '100%',
                  borderRadius: 2,
                  borderColor: selected ? t.color : 'divider',
                  borderWidth: selected ? 2 : 1,
                  borderLeftWidth: 4,
                  borderLeftColor: t.color,
                  bgcolor: selected ? t.bg : 'background.paper',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  '&:hover': {
                    borderColor: t.color,
                    bgcolor: t.bg,
                  },
                }}
              >
                {selected && (
                  <TaskAltOutlinedIcon
                    sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: t.color }}
                  />
                )}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar
                    sx={{
                      bgcolor: selected ? t.color : t.bg,
                      color: selected ? 'white' : t.color,
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                  >
                    <TypeIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: selected ? t.color : 'text.primary' }}>
                      {t.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                      {t.desc}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          )
        })}
      </Grid>
    </Stack>
  )

  // ─── Step 2: Upload proofs ────────────────────────────────────────────────────
  const renderStep2 = () => {
    const tatRemaining = eligibility?.tatRemainingHours ?? eligibility?.tat_remaining_hours ?? eligibility?.remainingHours
    const tatDeadline = eligibility?.tatDeadlineAt || eligibility?.tatExpiry || eligibility?.tat_expiry || eligibility?.expiresAt
    const completedCount = requirements.filter((r) =>
      r.isText ? !!proofTexts[r.key]?.trim() && (r.key !== 'ALT_MOBILE' || /^\d{10}$/.test(proofTexts[r.key])) : (proofFiles[r.key]?.length || 0) > 0
    ).length
    const totalRequired = requirements.filter((r) => r.required).length

    return (
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Upload supporting evidence</Typography>
            <Typography variant="body2" color="text.secondary">
              Complete proofs lead to faster resolutions
            </Typography>
          </Box>
          {totalRequired > 0 && (
            <Chip
              label={`${completedCount} / ${totalRequired} uploaded`}
              size="small"
              color={completedCount === totalRequired ? 'success' : 'default'}
              variant={completedCount === totalRequired ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, minWidth: 100 }}
            />
          )}
        </Stack>

        {(tatRemaining != null || tatDeadline) && (
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2, py: 0.5 }}>
            <strong>Claim window open</strong>
            {tatRemaining != null && ` · ${tatRemaining} hrs remaining`}
            {tatDeadline && ` · Closes ${tatDeadline}`}
          </Alert>
        )}

        {requirements.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No mandatory proof documents are required for this claim type.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add supporting remarks on the next step to strengthen your claim.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {requirements.map((r, idx) => {
              const files = proofFiles[r.key] || []
              const isComplete = r.isText
                ? !!proofTexts[r.key]?.trim() && (r.key !== 'ALT_MOBILE' || /^\d{10}$/.test(proofTexts[r.key]))
                : files.length > 0
              return (
                <Paper
                  key={r.key}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isComplete ? 'success.main' : 'divider',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}
                >
                  {/* Proof header */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      px: 2,
                      py: 1.25,
                      bgcolor: isComplete ? 'success.50' : 'grey.50',
                      borderBottom: '1px solid',
                      borderColor: isComplete ? 'success.100' : 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        bgcolor: isComplete ? 'success.main' : 'grey.300',
                        color: isComplete ? 'white' : 'text.secondary',
                        flexShrink: 0,
                        fontSize: 11,
                      }}
                    >
                      {isComplete ? <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> : idx + 1}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                      {r.label}
                      {r.required && <Typography component="span" color="error" sx={{ ml: 0.25 }}>*</Typography>}
                    </Typography>
                    {r.help && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {r.help}
                      </Typography>
                    )}
                    {isComplete && (
                      <Chip label="Done" size="small" color="success" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                    )}
                  </Stack>

                  {/* Proof input area */}
                  <Box sx={{ p: 2 }}>
                    {r.isText ? (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={r.help || 'Enter value'}
                        value={proofTexts[r.key] || ''}
                        onChange={(e) => {
                          const val = r.key === 'ALT_MOBILE'
                            ? e.target.value.replace(/\D/g, '').slice(0, 10)
                            : e.target.value
                          setProofTexts((p) => ({ ...p, [r.key]: val }))
                        }}
                        inputProps={r.key === 'ALT_MOBILE' ? { inputMode: 'numeric', maxLength: 10 } : undefined}
                        error={r.key === 'ALT_MOBILE' && !!proofTexts[r.key] && proofTexts[r.key].length !== 10}
                        helperText={r.key === 'ALT_MOBILE' && !!proofTexts[r.key] && proofTexts[r.key].length !== 10 ? 'Must be a 10-digit number' : undefined}
                      />
                    ) : (
                      <>
                        <FileDropzone
                          accept={r.accept}
                          onDrop={(list) => handleFiles(r.key, list)}
                          disabled={files.length >= 5}
                        />
                        {files.length > 0 && (
                          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                            {files.map((f, i) => (
                              <Stack
                                key={`${f.name}-${i}`}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                  px: 1.5,
                                  py: 0.75,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'grey.200',
                                }}
                              >
                                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                                  {fileIconFor(f)}
                                </Box>
                                <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                                  {f.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                  {formatBytes(f.size)}
                                </Typography>
                                <IconButton size="small" onClick={() => removeFile(r.key, i)} sx={{ ml: 0.5 }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                      </>
                    )}
                  </Box>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Stack>
    )
  }

  // ─── Step 3: Review & submit ──────────────────────────────────────────────────
  const renderStep3 = () => {
    const selectedType = CLAIM_TYPES.find((t) => t.key === claimType)
    const totalFiles = Object.values(proofFiles).reduce((sum, arr) => sum + (arr?.length || 0), 0)
    const requirementsByKey = Object.fromEntries(requirements.map((r) => [r.key, r.label]))

    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Review & submit</Typography>
          <Typography variant="body2" color="text.secondary">
            Check everything looks correct, add your remarks, then submit.
          </Typography>
        </Box>

        {/* Claim summary */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {/* Coloured top strip */}
          <Box sx={{ height: 4, bgcolor: selectedType?.color || 'primary.main' }} />
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              {selectedType && (
                <Avatar sx={{ bgcolor: selectedType.bg, color: selectedType.color, width: 36, height: 36 }}>
                  <selectedType.Icon sx={{ fontSize: 18 }} />
                </Avatar>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>{selectedType?.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {shipmentType} · AWB {shipment?.awb_number}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={claimType}
                sx={{ bgcolor: selectedType?.bg, color: selectedType?.color, fontWeight: 700, fontSize: 11 }}
              />
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow label="Order ID" value={shipment?.reference || shipment?.orderId} mono />
                <InfoRow label="Courier" value={shipment?.courierDetail?.parent || shipment?.courier} />
                <InfoRow label="Invoice Value" value={shipment?.invoice_value != null ? formatCurrency(shipment.invoice_value) : '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow label="Files attached" value={`${totalFiles} file${totalFiles !== 1 ? 's' : ''}`} />
                {Object.entries(proofTexts).filter(([, v]) => v).map(([k, v]) => (
                  <InfoRow key={k} label={requirementsByKey[k] || k.replace(/_/g, ' ')} value={v} />
                ))}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Attached files compact list */}
        {totalFiles > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
              Attached files ({totalFiles})
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(proofFiles).flatMap(([k, arr]) =>
                (arr || []).map((f, i) => (
                  <Grid item xs={12} sm={6} key={`${k}-${i}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ px: 1.5, py: 0.75, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}
                    >
                      <Box sx={{ color: 'primary.main', display: 'flex' }}>{fileIconFor(f)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {requirementsByKey[k] || k.replace(/_/g, ' ')} · {formatBytes(f.size)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        )}

        {/* Remarks — required before submit */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            Supporting remarks <Typography component="span" color="error">*</Typography>
          </Typography>
          <TextField
            multiline
            minRows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Describe the issue — what the consignee reported, what was received, any other context that helps the claims team"
            fullWidth
            error={!remarks?.trim()}
            helperText={!remarks?.trim() ? 'Remarks are required before you can submit' : ''}
          />
        </Box>
      </Stack>
    )
  }

  // ─── Success screen ───────────────────────────────────────────────────────────
  const renderSuccess = () => {
    const claimId = submittedClaim?.claimId || submittedClaim?.claim_id || submittedClaim?.id
    return (
      <Stack alignItems="center" spacing={3} sx={{ py: 3, textAlign: 'center' }}>
        {/* Icon */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 0 12px rgba(76,175,80,0.12)',
          }}
        >
          <VerifiedOutlinedIcon sx={{ fontSize: 38, color: 'white' }} />
        </Box>

        {/* Title */}
        <Box>
          <Typography variant="h6" fontWeight={700}>Claim submitted successfully</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A confirmation has been sent to your registered email address.
          </Typography>
        </Box>

        {/* Claim ID highlight */}
        {claimId && (
          <Box
            sx={{
              px: 3,
              py: 1.5,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              Your Claim ID
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
              {claimId}
            </Typography>
          </Box>
        )}

        {/* Details grid */}
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, width: '100%', maxWidth: 500, textAlign: 'left' }}>
          <Grid container spacing={1.5}>
            {submittedClaim?.claimStatus && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                <Chip label={submittedClaim.claimStatus} size="small" color="success" sx={{ fontWeight: 700, mt: 0.25 }} />
              </Grid>
            )}
            {submittedClaim?.claimType && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Claim Type</Typography>
                <Typography variant="body2" fontWeight={600}>{submittedClaim.claimType}</Typography>
              </Grid>
            )}
            {submittedClaim?.invoiceValue != null && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Invoice Value</Typography>
                <Typography variant="body2" fontWeight={600}>₹{submittedClaim.invoiceValue}</Typography>
              </Grid>
            )}
            {submittedClaim?.claimRegisteredDate && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Registered On</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {new Date(submittedClaim.claimRegisteredDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </Typography>
              </Grid>
            )}
            {submittedClaim?.claimSubmitByDate && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Submit Docs By</Typography>
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  {new Date(submittedClaim.claimSubmitByDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </Typography>
              </Grid>
            )}
            {submittedClaim?.courierId && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Courier</Typography>
                <Typography variant="body2" fontWeight={600}>{submittedClaim.courierId}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Actions */}
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" onClick={onClose} size="large">
            Close
          </Button>
          {claimId && (
            <Button
              variant="contained"
              size="large"
              component={Link}
              to={`/claims/${claimId}`}
              onClick={onClose}
            >
              View Claim
            </Button>
          )}
        </Stack>
      </Stack>
    )
  }

  // ─── Dialog shell ─────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 38, height: 38 }}>
            <GavelOutlinedIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Raise a Claim
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AWB <strong>{shipment?.awb_number || '—'}</strong>
              {shipment?.courierDetail?.parent && ` · ${shipment.courierDetail.parent}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Progress bar (submitting) ── */}
      {submitting && <LinearProgress />}

      {/* ── Step progress ── */}
      {!submittedClaim && renderStepHeader()}

      {/* ── Content ── */}
      <DialogContent sx={{ p: 3, bgcolor: 'background.paper' }}>
        {submittedClaim ? renderSuccess() : (
          <>
            {active === 0 && renderStep0()}
            {active === 1 && renderStep1()}
            {active === 2 && renderStep2()}
            {active === 3 && renderStep3()}
          </>
        )}
      </DialogContent>

      {/* ── Footer actions ── */}
      {!submittedClaim && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Step {active + 1} of {STEPS.length} — {STEPS[active]?.label}
          </Typography>
          <Button onClick={onClose} disabled={submitting} color="inherit" size="medium">
            Cancel
          </Button>
          {active > 0 && (
            <Button
              startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '14px !important' }} />}
              onClick={() => setActive((s) => s - 1)}
              disabled={submitting}
              variant="outlined"
              size="medium"
            >
              Back
            </Button>
          )}
          {active < STEPS.length - 1 ? (
            <LoadingButton
              loading={checkingEligibility}
              loadingPosition="end"
              endIcon={<ArrowForwardIosIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleContinue}
              disabled={!canGoNext() || checkingEligibility}
              variant="contained"
              size="medium"
              sx={{ minWidth: 130 }}
            >
              {checkingEligibility ? 'Checking…' : 'Continue'}
            </LoadingButton>
          ) : (
            <LoadingButton
              loading={submitting}
              variant="contained"
              onClick={submit}
              startIcon={<CheckCircleOutlineIcon />}
              size="medium"
              disabled={!remarks?.trim()}
              sx={{ minWidth: 150 }}
            >
              Submit Claim
            </LoadingButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}
